import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from '@features/auth/contexts/AuthContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Textarea } from "@shared/components/ui/textarea";
import { Badge } from "@shared/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/components/ui/table";
import { toast } from "sonner";
import { Upload, Download, Plus, Trash2, Check, X } from "lucide-react";
import API from "@core/api/api";
import { bulkCreateStockHistory } from '@features/products/services/productService';

export function BulkStockHistoryImport({ productId }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [movements, setMovements] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Check if user can edit stock movements (admin or manager only)
  const canEditStock = user?.role === "admin" || user?.role === "manager";

  // Bulk create stock history mutation
  const bulkCreateStockHistoryMutation = useMutation({
    mutationFn: async (movements) => {
      return await bulkCreateStockHistory(movements);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["stockHistory", productId]);
      queryClient.invalidateQueries(["stockCalculation", productId]);
      toast.success(t("Bulk stock history imported successfully"));
      setIsDialogOpen(false);
      setMovements([]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message ||
          t("Failed to import bulk stock history")
      );
    },
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Parse CSV or Excel file here - currently missing implementation
        // For now, return empty array to prevent errors
        const data = [];

        const processedMovements = data.map((row, index) => ({
          id: index + 1,
          productId,
          adjustment: parseFloat(row.adjustment || 0),
          adjustmentType: row.adjustmentType || "manual",
          reason: row.reason || "",
          notes: row.notes || "",
          referenceId: row.referenceId || "",
          referenceType: row.referenceType || "",
          location: row.location || "default",
          batchNumber: row.batchNumber || "",
          unitCost: row.unitCost ? parseFloat(row.unitCost) : null,
          totalCost: row.totalCost ? parseFloat(row.totalCost) : null,
          isValid: true,
          errors: [],
        }));

        setMovements(processedMovements);
        toast.success(t("File uploaded successfully"));
      } catch (error) {
        toast.error(t("Failed to process file"));
        console.error("File processing error:", error);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleManualAdd = () => {
    const newMovement = {
      id: movements.length + 1,
      productId,
      adjustment: 0,
      adjustmentType: "manual",
      reason: "",
      notes: "",
      referenceId: "",
      referenceType: "",
      location: "default",
      batchNumber: "",
      unitCost: null,
      totalCost: null,
      isValid: true,
      errors: [],
    };
    setMovements([...movements, newMovement]);
  };

  const handleMovementChange = (id, field, value) => {
    setMovements(
      movements.map((movement) =>
        movement.id === id ? { ...movement, [field]: value } : movement
      )
    );
  };

  const handleRemoveMovement = (id) => {
    setMovements(movements.filter((movement) => movement.id !== id));
  };

  const validateMovements = () => {
    const validatedMovements = movements.map((movement) => {
      const errors = [];

      if (!movement.adjustment || isNaN(movement.adjustment)) {
        errors.push(t("Adjustment is required and must be a number"));
      }

      if (!movement.reason.trim()) {
        errors.push(t("Reason is required"));
      }

      if (movement.unitCost && isNaN(movement.unitCost)) {
        errors.push(t("Unit cost must be a number"));
      }

      if (movement.totalCost && isNaN(movement.totalCost)) {
        errors.push(t("Total cost must be a number"));
      }

      return {
        ...movement,
        isValid: errors.length === 0,
        errors,
      };
    });

    setMovements(validatedMovements);
    return validatedMovements.every((movement) => movement.isValid);
  };

  const handleImport = () => {
    if (!validateMovements()) {
      toast.error(t("Please fix validation errors before importing"));
      return;
    }

    const movementsToImport = movements.map((movement) => ({
      productId: movement.productId,
      adjustment: movement.adjustment,
      adjustmentType: movement.adjustmentType,
      reason: movement.reason,
      notes: movement.notes,
      referenceId: movement.referenceId,
      referenceType: movement.referenceType,
      location: movement.location,
      batchNumber: movement.batchNumber,
      unitCost: movement.unitCost,
      totalCost: movement.totalCost,
    }));

    bulkCreateStockHistoryMutation.mutate(movementsToImport);
  };

  const downloadTemplate = () => {
    const template = [
      {
        adjustment: 10,
        adjustmentType: "manual",
        reason: "Initial stock setup",
        notes: "Initial inventory",
        referenceId: "",
        referenceType: "",
        location: "default",
        batchNumber: "",
        unitCost: 5.5,
        totalCost: 55.0,
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(template);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");

    XLSX.writeFile(workbook, "stock_history_template.xlsx");
  };

  const getAdjustmentTypeColor = (type) => {
    const colors = {
      manual: "bg-chart-1/10 text-chart-1 border-chart-1/20",
      pickup: "bg-chart-2/10 text-chart-2 border-chart-2/20",
      package: "bg-destructive/10 text-destructive border-destructive/20",
      correction: "bg-chart-4/10 text-chart-4 border-chart-4/20",
      import: "bg-chart-5/10 text-chart-5 border-chart-5/20",
      export: "bg-chart-3/10 text-chart-3 border-chart-3/20",
      damage: "bg-destructive/10 text-destructive border-destructive/20",
      expiry: "bg-muted text-muted-foreground border-border",
    };
    return colors[type] || "bg-muted text-muted-foreground border-border";
  };

  // If user doesn't have permission, don't render the component
  if (!canEditStock) {
    return null;
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          {t("Bulk Import")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("Bulk Import Stock History")}</DialogTitle>
          <DialogDescription>
            {t(
              "Import multiple stock movements at once using Excel or manual entry"
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t("Upload Excel File")}
              </CardTitle>
              <CardDescription>
                {t("Upload an Excel file with stock movement data")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="flex-1"
                />
                <Button variant="outline" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  {t("Template")}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                {t("Download the template to see the required format")}
              </p>
            </CardContent>
          </Card>

          {/* Manual Entry */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t("Manual Entry")}</CardTitle>
              <CardDescription>
                {t("Add movements manually or edit uploaded data")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleManualAdd} className="mb-4">
                <Plus className="h-4 w-4 mr-2" />
                {t("Add Movement")}
              </Button>

              {movements.length > 0 && (
                <div className="space-y-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("Type")}</TableHead>
                        <TableHead>{t("Adjustment")}</TableHead>
                        <TableHead>{t("Reason")}</TableHead>
                        <TableHead>{t("Location")}</TableHead>
                        <TableHead>{t("Status")}</TableHead>
                        <TableHead>{t("Actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {movements.map((movement) => (
                        <TableRow key={movement.id}>
                          <TableCell>
                            <Select
                              value={movement.adjustmentType}
                              onValueChange={(value) =>
                                handleMovementChange(
                                  movement.id,
                                  "adjustmentType",
                                  value
                                )
                              }
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="manual">
                                  {t("Manual")}
                                </SelectItem>
                                <SelectItem value="pickup">
                                  {t("Pickup")}
                                </SelectItem>
                                <SelectItem value="package">
                                  {t("Package")}
                                </SelectItem>
                                <SelectItem value="correction">
                                  {t("Correction")}
                                </SelectItem>
                                <SelectItem value="import">
                                  {t("Import")}
                                </SelectItem>
                                <SelectItem value="export">
                                  {t("Export")}
                                </SelectItem>
                                <SelectItem value="damage">
                                  {t("Damage")}
                                </SelectItem>
                                <SelectItem value="expiry">
                                  {t("Expiry")}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={movement.adjustment}
                              onChange={(e) =>
                                handleMovementChange(
                                  movement.id,
                                  "adjustment",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-20"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={movement.reason}
                              onChange={(e) =>
                                handleMovementChange(
                                  movement.id,
                                  "reason",
                                  e.target.value
                                )
                              }
                              placeholder={t("Reason")}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={movement.location}
                              onChange={(e) =>
                                handleMovementChange(
                                  movement.id,
                                  "location",
                                  e.target.value
                                )
                              }
                              placeholder={t("Location")}
                            />
                          </TableCell>
                          <TableCell>
                            {movement.isValid ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <X className="h-4 w-4 text-red-600" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveMovement(movement.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Validation Errors */}
                  {movements.some((movement) => !movement.isValid) && (
                    <Card className="border-red-200 bg-red-50">
                      <CardHeader>
                        <CardTitle className="text-red-800 text-sm">
                          {t("Validation Errors")}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {movements.map(
                            (movement) =>
                              !movement.isValid &&
                              movement.errors.map((error, index) => (
                                <div
                                  key={`${movement.id}-${index}`}
                                  className="text-red-700 text-sm"
                                >
                                  {t("Movement")} {movement.id}: {error}
                                </div>
                              ))
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Summary */}
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-muted-foreground">
                      {t("Total movements")}: {movements.length}
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline">
                        {movements.filter((m) => m.isValid).length} {t("valid")}
                      </Badge>
                      <Badge variant="outline" className="text-red-600">
                        {movements.filter((m) => !m.isValid).length}{" "}
                        {t("invalid")}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleImport}
            disabled={
              movements.length === 0 || bulkCreateStockHistoryMutation.isPending
            }
          >
            {bulkCreateStockHistoryMutation.isPending
              ? t("Importing...")
              : t("Import")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
